import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import Icon from "new_components/atoms/Icon";
import Text from "new_components/atoms/Text";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import { createPortal } from "react-dom";
import typography from "styles/typography";
import colors from "styles/colors";

// Wrapper for the chat history list
const ChatHistoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  /* margin-top: 50px; removed */
  /* overflow-x: hidden; removed */
  padding: 16px 0;
  background-color: transparent;
  /* border-radius: 8px; removed if modal container already provides rounding */
`;

const ChatAction = styled.div`
  display: flex;
  align-items: center;
  /* opacity: 0; */
  /* transition: opacity 0.2s ease; */
  margin-left: auto;
  padding-left: 8px;
`;

const ChatHistoryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  cursor: pointer;
  margin-bottom: 8px;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const ActionIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #e0e0e0;
  }
`;

// Chat name and last updated info
const CardInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-width: 0; // Allows text to shrink if needed
`;

const ChatText = styled(Text)`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  line-height: 20px;
  margin-right: 8px;
`;

/* Context Menu */
const ContextMenu = styled.div`
  position: absolute;
  top: ${(props) => props.y}px;
  left: ${(props) => props.x}px;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 9999; /* Keep above everything else */
  padding: 8px;
`;

const MenuItem = styled.div`
  padding: 6px 10px;
  cursor: pointer;
  &:hover {
    background: #eee;
  }
`;

const CenteredTextMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 200px);
`;

function formatHistory(history) {
  return history?.map((item) => {
    const name =
      item?.name
        ?.split("_")
        ?.map((word, index) => {
          if (index === 0) {
            return word.charAt(0).toUpperCase() + word.slice(1);
          }
          return word;
        })
        ?.join(" ") + "..." || "Creating new chat...";

    return {
      name: name,
      id: item.id,
      token: item.token,
      employeeId: item.employee_id,
    };
  });
}

const ChatHistory = ({
  onRoomSelect,
  selectedRoomId,
  chatRooms,
  onDeleteRoom,
}) => {
  const [chatHistory, setChatHistory] = useState([]);
  // State for context menu
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    chatId: null,
  });

  const menuRef = useRef();

  useEffect(() => {
    // Fetch chat topics from the API
    if (chatRooms?.chat_topics?.length > 0) {
      const formattedHistory = formatHistory(chatRooms.chat_topics);
      setChatHistory(formattedHistory);
    } else {
      setChatHistory([]);
    }
  }, [chatRooms]);

  // Close menu if user clicks anywhere outside the menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setContextMenu({ ...contextMenu, visible: false });
      }
    };
    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [contextMenu]);

  // Handler: open custom context menu on right-click
  const handleContextMenu = (e, chatId) => {
    e.preventDefault(); // stop the native menu
    setContextMenu({
      visible: true,
      x: e.pageX,
      y: e.pageY,
      chatId,
    });
  };

  // Action: delete chat
  const handleDeleteChat = () => {
    const { chatId } = contextMenu;
    // ... perform your delete logic here
    // e.g. call an API or dispatch a Redux action
    onDeleteRoom(chatId);
    // Close the menu after delete
    setContextMenu({ ...contextMenu, visible: false });
  };

  return (
    <ChatHistoryWrapper>
      {(chatHistory?.length > 0 && (
        <>
          <div style={{ height: 10 }} />
          {chatHistory.map((chat) => {
            const isActive = chat.id === selectedRoomId;

            return (
              <ChatHistoryRow
                key={chat.id}
                isActive={isActive}
                onClick={() => onRoomSelect(chat?.id)}
                // onContextMenu={(e) => handleContextMenu(e, chat.id)}
              >
                <CardInfo>
                  <ChatText
                    typographyStyle={typography.caption.regular}
                    color={isActive ? colors.primary[500] : null}
                  >
                    {chat.name}
                  </ChatText>
                  <ChatAction>
                    <ActionIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteRoom(chat?.id);
                      }}
                    >
                      <Icon
                        size={20}
                        name={ICON_SET.delete}
                        style={{ cursor: "pointer" }}
                      />
                    </ActionIcon>
                  </ChatAction>
                </CardInfo>
              </ChatHistoryRow>
            );
          })}
          {contextMenu.visible &&
            createPortal(
              <ContextMenu ref={menuRef} x={contextMenu.x} y={contextMenu.y}>
                <MenuItem onClick={handleDeleteChat}>
                  <Icon name={ICON_SET.delete} style={{ marginRight: 8 }} />
                  Delete Chat
                </MenuItem>
                {/* Add more actions as you wish */}
              </ContextMenu>,
              document.body
            )}
        </>
      )) || (
        <CenteredTextMessage>
          <Text
            typographyStyle={typography?.caption?.semibold}
            color={colors.text.gray}
          >
            No chat history. Create a chat to get started.
          </Text>
        </CenteredTextMessage>
      )}
    </ChatHistoryWrapper>
  );
};

ChatHistory.propTypes = {
  onRoomSelect: PropTypes.func.isRequired, // Function to handle chat selection
  selectedRoomId: PropTypes.number, // ID of the currently active chat
};

ChatHistory.defaultProps = {
  selectedRoomId: null, // No chat selected by default
};

export default ChatHistory;
